import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Truck, 
  Route, 
  Map, 
  History, 
  BarChart3, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  LogOut,
  User,
  CalendarOff,
  MessageSquare,
  KeyRound,
  BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const MENU_ITEMS = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/solicitudes', icon: FileText, label: 'Solicitudes' },
  { path: '/admin/vehiculos', icon: Truck, label: 'Vehículos' },
  { path: '/admin/rutas', icon: Route, label: 'Rutas' },
  { path: '/admin/mapa', icon: Map, label: 'Mapa' },
  { path: '/admin/festivos', icon: CalendarOff, label: 'Festivos' },
  { path: '/admin/whatsapp', icon: MessageSquare, label: 'WhatsApp' },
  { path: '/admin/historico', icon: History, label: 'Histórico' },
  { path: '/admin/resumen', icon: BarChart3, label: 'Resumen Diario' },
  { path: '/admin/manual', icon: BookOpen, label: 'Manual' },
];

export function AdminSidebar({ collapsed, onToggleCollapse }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [syncLoading, setSyncLoading] = useState(false);

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleSync = async () => {
    setSyncLoading(true);
    // TODO: Implement ArcGIS sync later
    setTimeout(() => {
      setSyncLoading(false);
      // toast.success('Sincronización iniciada');
    }, 1000);
  };

  return (
    <div
      className={`bg-[#374B67] text-white flex flex-col transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
      data-testid="admin-sidebar"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        {!collapsed && (
          <div>
            <h2 className="font-semibold text-sm">Backoffice</h2>
            <p className="text-xs text-white/60">Majadahonda</p>
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {MENU_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                active
                  ? 'bg-[#009940] text-white shadow-md'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              title={collapsed ? item.label : ''}
            >
              <Icon size={20} className="flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* ArcGIS Sync Button */}
      <div className="p-3 border-t border-white/10">
        <Button
          onClick={handleSync}
          disabled={syncLoading}
          className={`w-full bg-[#69C7EA] hover:bg-[#5AB5D8] text-[#374B67] font-semibold ${
            collapsed ? 'px-0' : ''
          }`}
          data-testid="sidebar-sync-arcgis"
          title={collapsed ? 'Sincronizar ArcGIS' : ''}
        >
          <RefreshCw size={18} className={syncLoading ? 'animate-spin' : ''} />
          {!collapsed && <span className="ml-2">Sincronizar ArcGIS</span>}
        </Button>
      </div>

      {/* User Section */}
      <div className="p-3 border-t border-white/10">
        {!collapsed ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
              <User size={16} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate" data-testid="sidebar-user-name">
                  {localStorage.getItem('admin_name') || 'Admin'}
                </p>
                <p className="text-xs text-white/60 truncate">Majadahonda</p>
              </div>
            </div>
            <Link
              to="/admin/cambiar-contrasena"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              data-testid="sidebar-change-password"
            >
              <KeyRound size={16} />
              <span>Cambiar contraseña</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-red-500/20 hover:text-red-300 transition-colors"
              data-testid="sidebar-logout"
            >
              <LogOut size={16} />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <Link
              to="/admin/cambiar-contrasena"
              className="w-full flex items-center justify-center p-2 rounded-lg text-white/80 hover:bg-white/10 transition-colors"
              data-testid="sidebar-change-password-icon"
              title="Cambiar contraseña"
            >
              <KeyRound size={18} />
            </Link>
            <button
              onClick={handleLogout}
              className="w-full p-2 rounded-lg text-white/80 hover:bg-red-500/20 hover:text-red-300 transition-colors"
              data-testid="sidebar-logout-icon"
              title="Cerrar Sesión"
            >
              <LogOut size={18} className="mx-auto" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
